import React, { useEffect, useState } from 'react'
import { Link } from 'react-router'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Box from '@mui/material/Box'
import LinkMui from '@mui/material/Link'
import useMediaQuery from '@mui/material/useMediaQuery'
import * as datefns from 'date-fns'
import { PressReleaseListItem } from '@finance-lab/utils/types'
import { PressReleaseApi } from '../../../api/PressReleasesApi'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { getPressReleaseLink } from '../../../utils/get-press-release-link'
import InfoBlock from '../../components/InfoBlock'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../i18n/localeUtils'

const pressReleaseApi = new PressReleaseApi()

const TITLE_MAX_LENGTH = 100
const TITLE_MAX_LENGTH_WIDE = 120
const WIDE_SCREEN_MIN_WIDTH = 1250

const truncateTitle = (title: string, maxLength: number) =>
    title.length > maxLength ? `${title.slice(0, maxLength)}...` : title

const formatTimeHHmm = (date: string | Date) => datefns.format(new Date(date), 'HH:mm')

export const CompanyRecentPressReleases = ({ companyId }: { companyId: string }) => {
    const { t } = useTranslation()
    const { language } = useAppLocale()
    const [pressReleases, setPressReleases] = useState<Omit<PressReleaseListItem, 'body'>[]>([])
    const isWideScreen = useMediaQuery(`(min-width:${WIDE_SCREEN_MIN_WIDTH}px)`)
    const titleMaxLength = isWideScreen ? TITLE_MAX_LENGTH_WIDE : TITLE_MAX_LENGTH

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await pressReleaseApi.allPressReleases(
                    { page: 0, pageSize: 3 },
                    { companyId, preferredLanguage: language },
                )

                setPressReleases(data.map(({ body, ...pr }) => pr))
            } catch (err) {
                console.error(err)
            }
        }

        void fetchData()
    }, [companyId, language])

    if (pressReleases.length === 0) {
        return null
    }

    const latestPressRelease = pressReleases[0]

    return (
        <InfoBlock title={t('pages.companies.pressReleases')} loadingElement={!pressReleases}>
            <Table size='small' sx={{ '& td': { border: 0, verticalAlign: 'middle' } }}>
                <TableBody>
                    {pressReleases.map((pr) => (
                        <TableRow
                            key={pr.id}
                            hover
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'none',
                                color: 'text.primary',
                                '&:hover': { textDecoration: 'none' },
                            }}
                            component={Link}
                            to={getPressReleaseLink(companyId, pr.id)}
                        >
                            <TableCell
                                sx={{
                                    width: 100,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatDateYYYYMMDD(pr.publishedAt, { locale: language })}
                            </TableCell>
                            <TableCell sx={{ width: 48, whiteSpace: 'nowrap' }}>
                                {formatTimeHHmm(pr.publishedAt)}
                            </TableCell>
                            <TableCell>
                                <Box component='span' title={pr.title}>
                                    {truncateTitle(pr.title, titleMaxLength)}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <LinkMui
                component={Link}
                to={getPressReleaseLink(companyId, latestPressRelease.id)}
                sx={{ display: 'inline-block', marginTop: 2, paddingLeft: 4 }}
            >
                {t('site.actions.showAll')}
            </LinkMui>
        </InfoBlock>
    )
}
